import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { placeholder } from "src/lib/emotion/placeholder.ts";

export const rootContainer = ({ width }: { width?: number }) =>
  css({
    display: "inline-flex",
    flexDirection: "column",
    minWidth: 60,
    width: width ?? "100%",
    height: "fit-content",
    position: "relative",
  });

export const rootNumberInputStyle =
  ({
    error,
    disabled,
    hasEndAdornment = false,
  }: {
    error?: string;
    disabled: boolean;
    hasEndAdornment?: boolean;
  }) =>
  ({
    borderRadiusV2,
    colorsV2,
    numberInputContainer,
    inputBorderV2,
    numberInputUnit,
    numberInput,
    inputV2Placeholder,
    inputColorV2,
    inputWeightV2,
  }: ExactTheme) =>
    css({
      position: "relative",
      display: "inline-flex",
      height: 37,
      width: "100%",
      willChange: "border, box-shadow",
      transition: "border 150ms, box-shadow 150ms, color 150ms",
      flexDirection: "row-reverse",
      overflow: "hidden",
      ...numberInputContainer,
      color: disabled ? inputColorV2.disabled : inputColorV2.normal,
      fontWeight: disabled ? inputWeightV2.disabled : inputWeightV2.normal,
      background: disabled ? colorsV2.grey[50] : colorsV2.white,
      cursor: disabled ? "not-allowed" : "auto",
      paddingRight: hasEndAdornment ? "35px" : 0,

      border: disabled
        ? inputBorderV2.solid.disabled.default
        : error
        ? inputBorderV2.solid.standard.error
        : inputBorderV2.solid.standard.default,

      ...(!disabled && {
        ":hover": {
          border: error
            ? inputBorderV2.solid.hover.error
            : inputBorderV2.solid.hover.default,
        },

        "&.rc-input-number-focused": {
          border: error
            ? inputBorderV2.solid.focused.error
            : inputBorderV2.solid.focused.default,
          boxShadow: error
            ? `0 0 0 1px${colorsV2.red[500]}`
            : `0 0 0 1px${colorsV2.blue[500]}`,
        },
      }),

      ".rc-input-number-handler-wrap": {
        flexBasis: 22,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderLeft: inputBorderV2.solid.standard.default,
        height: "100%",
        overflow: "hidden",
        borderTopRightRadius: !hasEndAdornment
          ? borderRadiusV2.primary
          : undefined,
        borderBottomRightRadius: !hasEndAdornment
          ? borderRadiusV2.primary
          : undefined,

        ".rc-input-number-handler": {
          height: "100%",
          display: "grid",
          placeContent: "center",
          padding: "0 1px 0 0",
          background: colorsV2.grey[50],
          transition: "background 0.2s",
          color: disabled ? colorsV2.grey[200] : colorsV2.grey[800],

          ...(!disabled && {
            ":hover": {
              background: colorsV2.grey[100],
            },
            ":active": {
              background: colorsV2.grey[200],

              svg: {
                transform: "scale(0.9)",
              },
            },
          }),
        },

        ".rc-input-number-handler-up": {
          borderBottom: inputBorderV2.solid.standard.default,
        },
      },

      ".rc-input-number-input-wrap": {
        display: "flex",
        flex: 1,

        ".rc-input-number-input": {
          width: "100%",
          height: "100%",
          border: 0,
          outline: 0,
          ...numberInput,
          fontWeight: disabled ? inputWeightV2.disabled : inputWeightV2.normal,
          color: disabled ? inputColorV2.disabled : inputColorV2.normal,
          background: "transparent",
          cursor: "inherit",
          ...placeholder(inputV2Placeholder),
        },

        ".unit": {
          ...numberInputUnit,
          alignContent: "center",
        },
      },
    });
